import { AlertTypeEnum, type Alert } from '@/api/alert/types/alert';
import { RouteName } from '@/router';
import { POST_VIEW_ENTRY_QUERY, POST_VIEW_PUSH_ENTRY } from '@/services/deep-link';

export type AlertNavigationSource = Pick<Alert, 'type' | 'data'>;

export type ResolveAlertNavigationOptions = {
  currentUserId?: string;
  isPreviewClick?: boolean;
  /** Tap came from a system notification, not from in-app navigation. */
  isPushOrigin?: boolean;
};

export const buildPostViewPath = (postId: string, authorId?: string | null, isPushOrigin = false): string => {
  const path = `/${RouteName.App}/${RouteName.UserProfile}/${RouteName.PostView}/${postId}`;
  const params = [
    ...(authorId ? [`userId=${authorId}`] : []),
    ...(isPushOrigin ? [`${POST_VIEW_ENTRY_QUERY}=${POST_VIEW_PUSH_ENTRY}`] : []),
  ];
  return params.length ? `${path}?${params.join('&')}` : path;
};

export const SYNTHETIC_CHAT_PUSH_ID_PREFIX = 'push:chat:';

export const buildSyntheticChatPushId = (chatId: string): string => `${SYNTHETIC_CHAT_PUSH_ID_PREFIX}${chatId}`;

/** Chat pushes are never Alert documents; ids starting with `push:` are local-only. */
export const isPersistedAlertId = (alertId: string): boolean => Boolean(alertId) && !alertId.startsWith('push:');

export const shouldMarkPushAlertRead = (alert: Pick<Alert, 'id' | 'type'>): boolean =>
  alert.type !== AlertTypeEnum.chat && isPersistedAlertId(alert.id);

const resolveChatPushChatId = (data: Alert['data'] | undefined): string | null => {
  const chatId = data?.chatId;
  return typeof chatId === 'string' && chatId ? chatId : null;
};

/** FCM nests the formatted alert as `data.data` (JSON string). */
export const parsePushAlert = (raw: string | undefined): Alert | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Alert & { _id?: string };
    if (parsed.type === AlertTypeEnum.chat) {
      const chatId = resolveChatPushChatId(parsed.data);
      if (!chatId) return null;
      return { ...parsed, id: parsed.id ?? parsed._id ?? buildSyntheticChatPushId(chatId) };
    }
    const id = parsed.id ?? parsed._id;
    if (!id) return null;
    return { ...parsed, id };
  } catch {
    return null;
  }
};

const resolvePostRelatedAlertPath = (
  type: AlertTypeEnum,
  data: Alert['data'],
  currentUserId?: string,
  isPushOrigin?: boolean
): string | null => {
  if (!data.postId) {
    if (type === AlertTypeEnum.post && data.userId) {
      // Profile fallback, not Post View — nothing to mark.
      return `/${RouteName.App}/${RouteName.Feed}/${RouteName.UserProfile}/${data.userId}`;
    }
    return null;
  }

  const authorId = data.authorId || (type === AlertTypeEnum.post ? data.userId : undefined) || currentUserId || null;

  return buildPostViewPath(data.postId, authorId, isPushOrigin);
};

/**
 * Shared tap target for push notifications, in-app alert rows, and socket toasts.
 * Returns null when the alert has no navigable destination (caller should stay put).
 */
export const resolveAlertNavigationPath = (
  alert: AlertNavigationSource,
  options: ResolveAlertNavigationOptions = {}
): string | null => {
  const { type, data } = alert;
  const { currentUserId, isPreviewClick, isPushOrigin } = options;

  switch (type) {
    case AlertTypeEnum.groupWorkoutDone:
    case AlertTypeEnum.groupInvite:
      return data.groupId ? `/${RouteName.App}/${RouteName.Group}/${data.groupId}` : null;
    case AlertTypeEnum.groupWorkout:
      return data.workoutEventId
        ? `/${RouteName.App}/${RouteName.Group}/${RouteName.Plan}/${data.workoutEventId}`
        : null;
    case AlertTypeEnum.post:
    case AlertTypeEnum.comment:
    case AlertTypeEnum.reaction:
      return resolvePostRelatedAlertPath(type, data, currentUserId, isPushOrigin);
    case AlertTypeEnum.workout:
      return data.workoutEventId
        ? `/${RouteName.App}/${RouteName.Feed}/${RouteName.Plan}/${data.workoutEventId}`
        : null;
    case AlertTypeEnum.chat: {
      if (!data.chatId) return null;
      const path = `/${RouteName.App}/${RouteName.Feed}/${RouteName.Chat}/${data.chatId}`;
      return isPushOrigin ? `${path}?${POST_VIEW_ENTRY_QUERY}=${POST_VIEW_PUSH_ENTRY}` : path;
    }
    case AlertTypeEnum.follow:
      if (isPreviewClick) {
        return data.userId ? `/${RouteName.App}/${RouteName.Feed}/${RouteName.UserProfile}/${data.userId}` : null;
      }
      return `/${RouteName.App}/${RouteName.Feed}/${RouteName.Notifications}`;
    case AlertTypeEnum.following:
      return data.userId ? `/${RouteName.App}/${RouteName.Feed}/${RouteName.UserProfile}/${data.userId}` : null;
    default:
      return `/${RouteName.App}/${RouteName.Feed}`;
  }
};
